/**
 * Notes/Records Service - Notion-style documentation
 *
 * Features:
 * - Hierarchical notes with parent/child relationships
 * - Icons and cover images
 * - Pinned notes
 * - Archive functionality
 */

import type { Note, LinkedEntity } from '../types';
import {
  getStore,
  addEntity,
  getEntity,
  getEntitiesByWorkspace,
  updateEntity,
  deleteEntity,
} from '../store';
import { generateId } from '../id';

export interface CreateNoteInput {
  workspaceId: string;
  title: string;
  content: string;
  tags?: string[];
  linkedEntities?: LinkedEntity[];
  icon?: string;
  coverImage?: string;
  parentId?: string;
  isPinned?: boolean;
}

export function createNote(input: CreateNoteInput): Note {
  const now = new Date();
  const note: Note = {
    id: generateId('note'),
    workspaceId: input.workspaceId,
    title: input.title,
    content: input.content,
    tags: input.tags ?? [],
    linkedEntities: input.linkedEntities ?? [],
    icon: input.icon,
    coverImage: input.coverImage,
    parentId: input.parentId,
    isPinned: input.isPinned ?? false,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  };

  return addEntity(getStore().notes, note);
}

export function getNote(id: string): Note | undefined {
  return getEntity(getStore().notes, id);
}

export function getNotesByWorkspace(workspaceId: string): Note[] {
  return getEntitiesByWorkspace(getStore().notes, workspaceId).filter(
    (note) => !note.isArchived
  );
}

export function getAllNotes(workspaceId: string): Note[] {
  return getEntitiesByWorkspace(getStore().notes, workspaceId);
}

export function getPinnedNotes(workspaceId: string): Note[] {
  return getNotesByWorkspace(workspaceId).filter((note) => note.isPinned);
}

export function getArchivedNotes(workspaceId: string): Note[] {
  return getEntitiesByWorkspace(getStore().notes, workspaceId).filter(
    (note) => note.isArchived
  );
}

export function getRootNotes(workspaceId: string): Note[] {
  return getNotesByWorkspace(workspaceId).filter((note) => !note.parentId);
}

export function getChildNotes(parentId: string): Note[] {
  const parent = getNote(parentId);
  if (!parent) return [];
  return getNotesByWorkspace(parent.workspaceId).filter(
    (note) => note.parentId === parentId
  );
}

export function searchNotes(workspaceId: string, query: string): Note[] {
  const notes = getNotesByWorkspace(workspaceId);
  const lowerQuery = query.toLowerCase();
  return notes.filter(
    (note) =>
      note.title.toLowerCase().includes(lowerQuery) ||
      note.content.toLowerCase().includes(lowerQuery) ||
      note.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
}

export function updateNote(
  id: string,
  updates: Partial<Omit<Note, 'id' | 'workspaceId' | 'createdAt'>>
): Note | undefined {
  return updateEntity(getStore().notes, id, updates);
}

export function deleteNote(id: string): boolean {
  return deleteEntity(getStore().notes, id);
}

export function archiveNote(id: string): Note | undefined {
  return updateNote(id, { isArchived: true });
}

export function unarchiveNote(id: string): Note | undefined {
  return updateNote(id, { isArchived: false });
}

export function pinNote(id: string): Note | undefined {
  return updateNote(id, { isPinned: true });
}

export function unpinNote(id: string): Note | undefined {
  return updateNote(id, { isPinned: false });
}

export function setNoteIcon(id: string, icon: string | undefined): Note | undefined {
  return updateNote(id, { icon });
}

export function setNoteCover(id: string, coverImage: string | undefined): Note | undefined {
  return updateNote(id, { coverImage });
}

export function setNoteParent(id: string, parentId: string | undefined): Note | undefined {
  return updateNote(id, { parentId });
}

export function addTagToNote(id: string, tag: string): Note | undefined {
  const note = getNote(id);
  if (!note) return undefined;
  if (!note.tags.includes(tag)) {
    note.tags.push(tag);
    return updateNote(id, { tags: note.tags });
  }
  return note;
}

export function removeTagFromNote(id: string, tag: string): Note | undefined {
  const note = getNote(id);
  if (!note) return undefined;
  const index = note.tags.indexOf(tag);
  if (index > -1) {
    note.tags.splice(index, 1);
    return updateNote(id, { tags: note.tags });
  }
  return note;
}

export function linkEntityToNote(
  noteId: string,
  entity: LinkedEntity
): Note | undefined {
  const note = getNote(noteId);
  if (!note) return undefined;
  const exists = note.linkedEntities.some(
    (e) => e.type === entity.type && e.id === entity.id
  );
  if (!exists) {
    note.linkedEntities.push(entity);
    return updateNote(noteId, { linkedEntities: note.linkedEntities });
  }
  return note;
}

export function unlinkEntityFromNote(
  noteId: string,
  entityType: LinkedEntity['type'],
  entityId: string
): Note | undefined {
  const note = getNote(noteId);
  if (!note) return undefined;
  const filtered = note.linkedEntities.filter(
    (e) => !(e.type === entityType && e.id === entityId)
  );
  if (filtered.length !== note.linkedEntities.length) {
    return updateNote(noteId, { linkedEntities: filtered });
  }
  return note;
}

export function getNoteTags(workspaceId: string): string[] {
  const notes = getNotesByWorkspace(workspaceId);
  const tagSet = new Set<string>();
  notes.forEach((note) => {
    note.tags.forEach((tag) => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
}

export function getNotesByTag(workspaceId: string, tag: string): Note[] {
  return getNotesByWorkspace(workspaceId).filter((note) =>
    note.tags.includes(tag)
  );
}
